/**
 * Swedish Forestry growth simulation utility.
 * Based on Agestam (1985) and standard SIS empirical models.
 */

const COEFFICIENTS = {
    Tall: { // Scots Pine
        a0: -1.2,
        a_vol: 0.75,
        a_age: -0.4,
        a_si: 0.015,
        max_vol_ha: 500 // m3sk/ha "roof"
    },
    Gran: { // Norway Spruce
        a0: -1.4,
        a_vol: 0.8,
        a_age: -0.5,
        a_si: 0.02,
        max_vol_ha: 700
    },
    Bjork: { // Birch
        a0: -1.3,
        a_vol: 0.7,
        a_age: -0.45,
        a_si: 0.012,
        max_vol_ha: 400
    }
};

/**
 * Calculates m3sk/ha increment for a specific species.
 */
function calculateSpeciesIncrement(species, currentVol, age, si) {
    const c = COEFFICIENTS[species] || COEFFICIENTS.Bjork;
    
    // Convert SI (e.g., T24) to numeric (24)
    const siNum = parseInt(si.replace(/[^0-9]/g, '')) || 24;

    // Safety check for log(0)
    const logVol = Math.log(Math.max(currentVol, 1));
    const logAge = Math.log(Math.max(age, 1));

    // ln(Iv) = a0 + a_vol*ln(V) + a_age*ln(Age) + a_si*SI
    const lnIv = c.a0 + (c.a_vol * logVol) + (c.a_age * logAge) + (c.a_si * siNum);
    let increment = Math.exp(lnIv);

    // Apply "roof" slowing
    if (currentVol > c.max_vol_ha * 0.8) {
        const factor = Math.max(0, (c.max_vol_ha - currentVol) / (c.max_vol_ha * 0.2));
        increment *= factor;
    }

    return increment;
}

/**
 * Simulates growth for a stand over time.
 * @param {Object} stand Current stand data
 * @param {Array} species Distribution [{ species, percentage }]
 * @param {Array} activities Planned activities
 * @param {Number} years Horizon (default 80)
 */
export function simulateGrowth(stand, species, activities = [], years = 80) {
    let currentAge = parseInt(stand.age) || 0;
    let currentVolHa = parseFloat(stand.volume_m3sk_ha) || 0;
    let currentStems = parseInt(stand.stems_per_ha) || 0;
    const area = parseFloat(stand.area_hectares) || parseFloat(stand.area) || 0;
    const si = stand.site_index || (stand.species?.[0]?.species === 'Gran' ? 'G24' : 'T24');

    const data = [];
    const currentYear = new Date().getFullYear();

    // Growth multiplier (can be enhanced by thinnings/clearing)
    let growthMultiplier = 1.0;
    let prevVolHa = currentVolHa;

    for (let t = 0; t <= years; t++) {
        const year = currentYear + t;
        
        // Record current state
        const siNum = parseInt(si.replace(/[^0-9]/g, '')) || 24;
        const estimatedHeight = Math.round(siNum * 1.2 * (1 - Math.exp(-0.02 * currentAge)) * 10) / 10;
        
        // Estimate DBH (cm) based on volume and height (simplified)
        const baEstimate = estimatedHeight > 0 ? (currentVolHa / (estimatedHeight * 0.45)) : 0;
        const dbhEstimate = (currentStems > 0 && baEstimate > 0) 
            ? Math.sqrt(baEstimate / (currentStems * Math.PI)) * 200 
            : (currentVolHa > 0 ? 10 + currentAge * 0.2 : 0);

        const annualIncrement = t === 0 ? 0 : Math.max(0, currentVolHa - prevVolHa);
        prevVolHa = currentVolHa;

        data.push({
            year,
            age: currentAge,
            volume_ha: Math.round(currentVolHa * 10) / 10,
            total_volume: Math.round(currentVolHa * area),
            stems_per_ha: Math.round(currentStems),
            height_m: estimatedHeight,
            basal_area_m2_ha: Math.round(baEstimate * 10) / 10,
            annual_increment_m3sk: Math.round(annualIncrement * 10) / 10,
            dbh_cm: Math.round(dbhEstimate * 10) / 10,
            species_data: species.map(s => ({
                species: s.species,
                volume_ha: Math.round((currentVolHa * (s.percentage / 100)) * 10) / 10
            }))
        });

        // Check for activities in this specific year
        const yearActivities = activities.filter(a => parseInt(a.planned_year) === year);
        yearActivities.forEach(activity => {
            if (activity.activity_type === 'Thinning') {
                const removalFactor = 0.3; 
                currentVolHa *= (1 - removalFactor);
                currentStems *= (1 - removalFactor);
                growthMultiplier *= 1.15; 
            } else if (activity.activity_type === 'Final Felling') {
                currentVolHa = 0;
                currentStems = 0;
                growthMultiplier = 1.0;
            } else if (activity.activity_type === 'Planting') {
                currentAge = 0;
                currentVolHa = 0;
                currentStems = parseInt(activity.calculation_params?.planting_density_per_ha) || 2500;
            } else if (activity.activity_type === 'Clearing') {
                currentStems *= 0.6;
                growthMultiplier *= 1.1;
            }
        });

        // Calculate total increment for the next year
        let totalIncrement = 0;
        if (species && species.length > 0) {
            species.forEach(s => {
                const specVol = currentVolHa * (s.percentage / 100);
                totalIncrement += calculateSpeciesIncrement(s.species, specVol, currentAge, si);
            });
        }

        // Apply global growth multiplier (from past treatments)
        currentVolHa += (totalIncrement * growthMultiplier);
        currentAge++;
    }

    return data;
}
